import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Card from '../../../../../shared/components/Card';
import userService, { UserWithRole, UserIpHistory } from '../../../../../shared/services/userService';
import usePageTranslation from '../../../../../shared/hooks/usePageTranslation';
import {
  getRoleBadgeColor,
  BasicInfoSection,
  AccountInfoSection,
  PremiumInfoSection,
  GameInfoSection,
  SecurityInfoSection,
  PreferencesSection,
} from './UserInfoSections';

interface UserDetailsModalProps {
  user: UserWithRole;
  onClose: () => void;
  onResetPassword?: (userId: number) => Promise<void>;
  resettingPassword?: boolean;
  onSanction?: (userId: number) => void;
  onForceLogout?: (userId: number) => Promise<void>;
  currentUserRoleId?: number;
  currentUserId?: number;
  onlineUserIds?: Set<number>;
  context?: 'roles' | 'users'; // 'roles' = gestion des rôles (CM uniquement), 'users' = gestion des utilisateurs (Modo, Admin, CM)
}

const UserDetailsModal: React.FC<UserDetailsModalProps> = ({
  user,
  onClose,
  onResetPassword,
  resettingPassword = false,
  onSanction,
  onForceLogout,
  currentUserRoleId,
  currentUserId,
  onlineUserIds,
  context = 'users'
}) => {
  const { t } = usePageTranslation('panel', 'UsersManagement');
  const [ipHistory, setIpHistory] = useState<UserIpHistory[]>([]);
  const [loadingIpHistory, setLoadingIpHistory] = useState(true);
  const [clearAuthLoading, setClearAuthLoading] = useState(false);
  const [clearAuthMessage, setClearAuthMessage] = useState<string | null>(null);
  const [clearAuthIsError, setClearAuthIsError] = useState(false);

  // Permissions pour les sanctions
  const canSanction = currentUserRoleId && (
    context === 'roles' 
      ? currentUserRoleId === 1 // CM uniquement dans gestion des rôles
      : currentUserRoleId <= 3 // Modo, Admin, CM dans gestion des utilisateurs
  );

  useEffect(() => {
    const loadIpHistory = async () => {
      try {
        setLoadingIpHistory(true);
        const response = await userService.getUserIpHistory(user.id);
        if (response.success) {
          setIpHistory(response.data || []);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'historique IP:', error);
        setIpHistory([]);
      } finally {
        setLoadingIpHistory(false);
      }
    };

    loadIpHistory();
  }, [user.id]);

  // getRoleBadgeColor importé depuis UserInfoSections

  const handleResetPassword = async () => {
    if (!onResetPassword) return;
    
    const confirmMessage = `${t('resetPassword.confirm')} ${user.username}?`;
    if (window.confirm(confirmMessage)) {
      await onResetPassword(user.id);
    }
  };

  const handleForceLogout = async () => {
    if (!onForceLogout) return;
    
    const confirmMessage = `Êtes-vous sûr de vouloir déconnecter de force ${user.username} ?`;
    if (window.confirm(confirmMessage)) {
      await onForceLogout(user.id);
    }
  };

  const canClearAuthRateLimit = Boolean(currentUserRoleId && currentUserRoleId <= 2);

  const handleClearAuthRateLimit = async () => {
    if (!canClearAuthRateLimit) return;
    const confirmMessage =
      t('authRateLimit.confirm') ||
      `Réinitialiser le blocage connexion/inscription (trop de tentatives) pour ${user.username} ?`;
    if (!window.confirm(confirmMessage)) return;
    setClearAuthLoading(true);
    setClearAuthMessage(null);
    setClearAuthIsError(false);
    try {
      const res = await userService.clearAuthRateLimit(user.id);
      if (res.success) {
        setClearAuthIsError(false);
        setClearAuthMessage(res.message || t('authRateLimit.success') || 'Blocage réinitialisé.');
      } else {
        setClearAuthIsError(true);
        setClearAuthMessage(res.error || t('authRateLimit.error') || 'Erreur.');
      }
    } catch (e: any) {
      setClearAuthIsError(true);
      setClearAuthMessage(e?.message || t('authRateLimit.error') || 'Erreur.');
    } finally {
      setClearAuthLoading(false);
    }
  };

  const isOnline = onlineUserIds && onlineUserIds.has(user.id);
  const canForceLogout = onForceLogout && isOnline && user.id !== currentUserId;

  return createPortal(
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-4 pt-24">
      <Card className="max-w-3xl w-full p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-bold text-white">
            {t('details.title')} - {user.username}
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="space-y-6">
          <BasicInfoSection user={user} t={t} />
          <AccountInfoSection user={user} t={t} />
          <PremiumInfoSection user={user} t={t} />

          <GameInfoSection user={user} t={t} />
          <SecurityInfoSection
            user={user}
            ipHistory={ipHistory}
            loadingIpHistory={loadingIpHistory}
            t={t}
          />
          <PreferencesSection user={user} t={t} />

          {/* Actions */}
          <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
            <h4 className="text-white font-semibold mb-4">{t('details.actions')}</h4>
            <div className="flex flex-wrap gap-3">
              {onResetPassword && (
                <button
                  onClick={handleResetPassword}
                  disabled={resettingPassword}
                  className="px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {resettingPassword ? t('resetPassword.sending') : t('resetPassword.send')}
                </button>
              )}
              {canClearAuthRateLimit && (
                <button
                  type="button"
                  onClick={handleClearAuthRateLimit}
                  disabled={clearAuthLoading}
                  className="px-4 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {clearAuthLoading
                    ? t('authRateLimit.loading') || 'Réinitialisation…'
                    : t('authRateLimit.button') || 'Débloquer tentatives connexion'}
                </button>
              )}
              {canForceLogout && (
                <button
                  onClick={handleForceLogout}
                  className="px-4 py-3 bg-orange-600 hover:bg-orange-700 text-white rounded-lg font-semibold transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                  </svg>
                  {t('actions.forceLogout') || 'Déconnecter de force'}
                </button>
              )}
              {onSanction && canSanction && (
                <button
                  onClick={() => onSanction(user.id)}
                  className="px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors"
                >
                  {t('details.sanction')}
                </button>
              )}
            </div>
            {onResetPassword && (
              <p className="text-gray-400 text-sm mt-2">
                {t('resetPassword.description')}
              </p>
            )}
            {clearAuthMessage && (
              <p className={`text-sm mt-3 ${clearAuthIsError ? 'text-red-300' : 'text-green-300'}`}>{clearAuthMessage}</p>
            )}
          </div>
        </div>

        <button
          onClick={onClose}
          className="mt-6 w-full px-4 py-3 bg-gray-700 text-white rounded-lg font-semibold hover:bg-gray-600 transition-colors"
        >
          {t('details.close')}
        </button>
      </Card>
    </div>,
    document.body
  );
};

export default UserDetailsModal;

